/**
 * VFX nodes — based on @engine/vfx API.
 *
 * VFXSystem (CPU particles):
 * - spawn(name, x, y, angle) → id
 * - muzzleFlash(x, y, angle), bulletImpact, explosion, debris, smoke, fire
 * - stop(id), clear()
 * - get(id), count, getAll()
 *
 * VFXEmitterManager (GPU emitters):
 * - createEmitter(assetPath, options) → id
 * - destroyEmitter(id), destroyAll()
 * - setPosition(id, x, y), setScale(id, x, y)
 * - play(id), stop(id), spawn(id, count)
 * - setFloat(id, index, value), setVec2, setVec3, setColor
 * - setTexture(id, slot, texture)
 * - getEmitter(id), getStats()
 */
import type { NodeBehaviour } from '@engine/redprint';
import { flowIn, flowOut, input, output } from '@engine/redprint';

// ============================================================================
// VFX SYSTEM (CPU Particles)
// ============================================================================

export const SpawnVFX: NodeBehaviour = {
  id: 'vfx.spawn',
  name: 'Spawn VFX',
  category: 'VFX',
  color: '#e74c3c',
  icon: 'Sparkles',
  description: 'Spawns a registered VFX effect by name.',
  keywords: ['particle', 'effect', 'spawn'],
  inputs: [
    flowIn(),
    input('name', 'Effect Name', 'string', 'explosion'),
    input('x', 'X', 'f32', 0),
    input('y', 'Y', 'f32', 0),
    input('angle', 'Angle (rad)', 'f32', 0),
  ],
  outputs: [flowOut(), output('id', 'Effect ID', 'i32')],
  compile(ctx) {
    return ctx.emit(
      'vfxSystem.spawn',
      [ctx.resolveInput('name'), ctx.resolveInput('x'), ctx.resolveInput('y'), ctx.resolveInput('angle')],
      'i32',
      true,
    );
  },
  execute(ctx) {
    const id = ctx.bridge.call('vfxSystem.spawn', ctx.getInput<string>('name'), ctx.getInput<number>('x'), ctx.getInput<number>('y'), ctx.getInput<number>('angle')) as number ?? 0;
    ctx.setOutput('id', id);
  },
};

export const MuzzleFlash: NodeBehaviour = {
  id: 'vfx.muzzleFlash',
  name: 'Muzzle Flash',
  category: 'VFX',
  color: '#e74c3c',
  icon: 'Zap',
  description: 'Spawns a muzzle flash effect.',
  keywords: ['gun', 'fire', 'weapon'],
  inputs: [flowIn(), input('x', 'X', 'f32', 0), input('y', 'Y', 'f32', 0), input('angle', 'Angle', 'f32', 0)],
  outputs: [flowOut(), output('id', 'Effect ID', 'i32')],
  compile(ctx) {
    return ctx.emit('vfxSystem.muzzleFlash', [ctx.resolveInput('x'), ctx.resolveInput('y'), ctx.resolveInput('angle')], 'i32', true);
  },
  execute(ctx) {
    const id = ctx.bridge.call('vfxSystem.muzzleFlash', ctx.getInput<number>('x'), ctx.getInput<number>('y'), ctx.getInput<number>('angle')) as number ?? 0;
    ctx.setOutput('id', id);
  },
};

export const BulletImpact: NodeBehaviour = {
  id: 'vfx.bulletImpact',
  name: 'Bullet Impact',
  category: 'VFX',
  color: '#e74c3c',
  icon: 'Target',
  description: 'Spawns a bullet impact effect.',
  keywords: ['hit', 'impact', 'projectile'],
  inputs: [flowIn(), input('x', 'X', 'f32', 0), input('y', 'Y', 'f32', 0), input('angle', 'Angle', 'f32', 0)],
  outputs: [flowOut(), output('id', 'Effect ID', 'i32')],
  compile(ctx) {
    return ctx.emit('vfxSystem.bulletImpact', [ctx.resolveInput('x'), ctx.resolveInput('y'), ctx.resolveInput('angle')], 'i32', true);
  },
  execute(ctx) {
    const id = ctx.bridge.call('vfxSystem.bulletImpact', ctx.getInput<number>('x'), ctx.getInput<number>('y'), ctx.getInput<number>('angle')) as number ?? 0;
    ctx.setOutput('id', id);
  },
};

export const Explosion: NodeBehaviour = {
  id: 'vfx.explosion',
  name: 'Explosion',
  category: 'VFX',
  color: '#e74c3c',
  icon: 'Bomb',
  description: 'Spawns an explosion effect.',
  keywords: ['boom', 'blast', 'explode'],
  inputs: [flowIn(), input('x', 'X', 'f32', 0), input('y', 'Y', 'f32', 0)],
  outputs: [flowOut(), output('id', 'Effect ID', 'i32')],
  compile(ctx) {
    return ctx.emit('vfxSystem.explosion', [ctx.resolveInput('x'), ctx.resolveInput('y')], 'i32', true);
  },
  execute(ctx) {
    const id = ctx.bridge.call('vfxSystem.explosion', ctx.getInput<number>('x'), ctx.getInput<number>('y')) as number ?? 0;
    ctx.setOutput('id', id);
  },
};

export const Debris: NodeBehaviour = {
  id: 'vfx.debris',
  name: 'Debris',
  category: 'VFX',
  color: '#e74c3c',
  icon: 'Layers',
  description: 'Spawns debris particles.',
  keywords: ['pieces', 'broken', 'shatter'],
  inputs: [flowIn(), input('x', 'X', 'f32', 0), input('y', 'Y', 'f32', 0), input('angle', 'Angle', 'f32', 0)],
  outputs: [flowOut(), output('id', 'Effect ID', 'i32')],
  compile(ctx) {
    return ctx.emit('vfxSystem.debris', [ctx.resolveInput('x'), ctx.resolveInput('y'), ctx.resolveInput('angle')], 'i32', true);
  },
  execute(ctx) {
    const id = ctx.bridge.call('vfxSystem.debris', ctx.getInput<number>('x'), ctx.getInput<number>('y'), ctx.getInput<number>('angle')) as number ?? 0;
    ctx.setOutput('id', id);
  },
};

export const Smoke: NodeBehaviour = {
  id: 'vfx.smoke',
  name: 'Smoke',
  category: 'VFX',
  color: '#e74c3c',
  icon: 'Cloud',
  description: 'Spawns smoke particles.',
  keywords: ['smoke', 'fog', 'dust'],
  inputs: [flowIn(), input('x', 'X', 'f32', 0), input('y', 'Y', 'f32', 0)],
  outputs: [flowOut(), output('id', 'Effect ID', 'i32')],
  compile(ctx) {
    return ctx.emit('vfxSystem.smoke', [ctx.resolveInput('x'), ctx.resolveInput('y')], 'i32', true);
  },
  execute(ctx) {
    const id = ctx.bridge.call('vfxSystem.smoke', ctx.getInput<number>('x'), ctx.getInput<number>('y')) as number ?? 0;
    ctx.setOutput('id', id);
  },
};

export const Fire: NodeBehaviour = {
  id: 'vfx.fire',
  name: 'Fire',
  category: 'VFX',
  color: '#e74c3c',
  icon: 'Flame',
  description: 'Spawns fire particles.',
  keywords: ['fire', 'flame', 'burn'],
  inputs: [flowIn(), input('x', 'X', 'f32', 0), input('y', 'Y', 'f32', 0)],
  outputs: [flowOut(), output('id', 'Effect ID', 'i32')],
  compile(ctx) {
    return ctx.emit('vfxSystem.fire', [ctx.resolveInput('x'), ctx.resolveInput('y')], 'i32', true);
  },
  execute(ctx) {
    const id = ctx.bridge.call('vfxSystem.fire', ctx.getInput<number>('x'), ctx.getInput<number>('y')) as number ?? 0;
    ctx.setOutput('id', id);
  },
};

export const StopEffect: NodeBehaviour = {
  id: 'vfx.stopEffect',
  name: 'Stop Effect',
  category: 'VFX',
  color: '#e74c3c',
  icon: 'X',
  description: 'Stops and removes a VFX effect by ID.',
  keywords: ['stop', 'remove', 'kill'],
  inputs: [flowIn(), input('id', 'Effect ID', 'i32', 0)],
  outputs: [flowOut()],
  compile(ctx) {
    ctx.emit('vfxSystem.stop', [ctx.resolveInput('id')], 'void', true);
  },
  execute(ctx) {
    ctx.bridge.call('vfxSystem.stop', ctx.getInput<number>('id'));
  },
};

export const ClearAllEffects: NodeBehaviour = {
  id: 'vfx.clearAll',
  name: 'Clear All Effects',
  category: 'VFX',
  color: '#e74c3c',
  icon: 'Trash2',
  description: 'Removes all active VFX effects.',
  keywords: ['clear', 'remove', 'all'],
  inputs: [flowIn()],
  outputs: [flowOut()],
  compile(ctx) {
    ctx.emit('vfxSystem.clear', [], 'void', true);
  },
  execute(ctx) {
    ctx.bridge.call('vfxSystem.clear');
  },
};

export const GetEffectCount: NodeBehaviour = {
  id: 'vfx.getCount',
  name: 'Get Effect Count',
  category: 'VFX',
  isPure: true,
  color: '#e74c3c',
  icon: 'Hash',
  description: 'Gets the number of active VFX effects.',
  keywords: ['count', 'active', 'effects'],
  inputs: [],
  outputs: [output('count', 'Count', 'i32')],
  compile(ctx) {
    return ctx.emit('vfxSystem.count', [], 'i32');
  },
  execute(ctx) {
    ctx.setOutput('count', ctx.bridge.call('vfxSystem.count') as number ?? 0);
  },
};

// ============================================================================
// VFX EMITTER MANAGER (GPU Particles)
// ============================================================================

export const CreateEmitter: NodeBehaviour = {
  id: 'vfx.createEmitter',
  name: 'Create Emitter',
  category: 'VFX',
  color: '#e74c3c',
  icon: 'Sparkles',
  description: 'Creates a GPU particle emitter from a VFX graph asset.',
  keywords: ['emitter', 'particle', 'gpu', 'spawn'],
  inputs: [
    flowIn(),
    input('asset', 'Asset Path', 'string', '/vfx/explosion.vfx'),
    input('x', 'X', 'f32', 0),
    input('y', 'Y', 'f32', 0),
    input('playOnStart', 'Play On Start', 'bool', true),
    input('loop', 'Loop', 'bool', true),
  ],
  outputs: [flowOut(), output('id', 'Emitter ID', 'string')],
  compile(ctx) {
    return ctx.emit(
      'vfxEmitterManager.createEmitter',
      [ctx.resolveInput('asset'), `{x: ${ctx.resolveInput('x')}, y: ${ctx.resolveInput('y')}, playOnStart: ${ctx.resolveInput('playOnStart')}, loop: ${ctx.resolveInput('loop')}}`],
      'string',
      true,
    );
  },
  execute(ctx) {
    const id = ctx.bridge.call('vfxEmitterManager.createEmitter', ctx.getInput<string>('asset'), ctx.getInput<number>('x'), ctx.getInput<number>('y'), ctx.getInput<boolean>('playOnStart'), ctx.getInput<boolean>('loop')) as string ?? '';
    ctx.setOutput('id', id);
  },
};

export const DestroyEmitter: NodeBehaviour = {
  id: 'vfx.destroyEmitter',
  name: 'Destroy Emitter',
  category: 'VFX',
  color: '#e74c3c',
  icon: 'X',
  description: 'Destroys a GPU particle emitter.',
  keywords: ['emitter', 'destroy', 'remove'],
  inputs: [flowIn(), input('id', 'Emitter ID', 'string', '')],
  outputs: [flowOut()],
  compile(ctx) {
    ctx.emit('vfxEmitterManager.destroyEmitter', [ctx.resolveInput('id')], 'void', true);
  },
  execute(ctx) {
    ctx.bridge.call('vfxEmitterManager.destroyEmitter', ctx.getInput<string>('id'));
  },
};

export const DestroyAllEmitters: NodeBehaviour = {
  id: 'vfx.destroyAllEmitters',
  name: 'Destroy All Emitters',
  category: 'VFX',
  color: '#e74c3c',
  icon: 'Trash2',
  description: 'Destroys all GPU particle emitters.',
  keywords: ['emitter', 'destroy', 'all', 'clear'],
  inputs: [flowIn()],
  outputs: [flowOut()],
  compile(ctx) {
    ctx.emit('vfxEmitterManager.destroyAll', [], 'void', true);
  },
  execute(ctx) {
    ctx.bridge.call('vfxEmitterManager.destroyAll');
  },
};

export const SetEmitterPosition: NodeBehaviour = {
  id: 'vfx.setEmitterPosition',
  name: 'Set Emitter Position',
  category: 'VFX',
  color: '#e74c3c',
  icon: 'Move',
  description: 'Sets the position of a GPU particle emitter.',
  keywords: ['emitter', 'position', 'move'],
  inputs: [flowIn(), input('id', 'Emitter ID', 'string', ''), input('x', 'X', 'f32', 0), input('y', 'Y', 'f32', 0)],
  outputs: [flowOut()],
  compile(ctx) {
    ctx.emit('vfxEmitterManager.setPosition', [ctx.resolveInput('id'), ctx.resolveInput('x'), ctx.resolveInput('y')], 'void', true);
  },
  execute(ctx) {
    ctx.bridge.call('vfxEmitterManager.setPosition', ctx.getInput<string>('id'), ctx.getInput<number>('x'), ctx.getInput<number>('y'));
  },
};

export const SetEmitterScale: NodeBehaviour = {
  id: 'vfx.setEmitterScale',
  name: 'Set Emitter Scale',
  category: 'VFX',
  color: '#e74c3c',
  icon: 'Maximize',
  description: 'Sets the scale of a GPU particle emitter.',
  keywords: ['emitter', 'scale', 'size'],
  inputs: [flowIn(), input('id', 'Emitter ID', 'string', ''), input('x', 'Scale X', 'f32', 1), input('y', 'Scale Y', 'f32', 1)],
  outputs: [flowOut()],
  compile(ctx) {
    ctx.emit('vfxEmitterManager.setScale', [ctx.resolveInput('id'), ctx.resolveInput('x'), ctx.resolveInput('y')], 'void', true);
  },
  execute(ctx) {
    ctx.bridge.call('vfxEmitterManager.setScale', ctx.getInput<string>('id'), ctx.getInput<number>('x'), ctx.getInput<number>('y'));
  },
};

export const PlayEmitter: NodeBehaviour = {
  id: 'vfx.playEmitter',
  name: 'Play Emitter',
  category: 'VFX',
  color: '#e74c3c',
  icon: 'Play',
  description: 'Starts playing a GPU particle emitter.',
  keywords: ['emitter', 'play', 'start'],
  inputs: [flowIn(), input('id', 'Emitter ID', 'string', '')],
  outputs: [flowOut()],
  compile(ctx) {
    ctx.emit('vfxEmitterManager.play', [ctx.resolveInput('id')], 'void', true);
  },
  execute(ctx) {
    ctx.bridge.call('vfxEmitterManager.play', ctx.getInput<string>('id'));
  },
};

export const StopEmitter: NodeBehaviour = {
  id: 'vfx.stopEmitter',
  name: 'Stop Emitter',
  category: 'VFX',
  color: '#e74c3c',
  icon: 'Square',
  description: 'Stops a GPU particle emitter.',
  keywords: ['emitter', 'stop', 'pause'],
  inputs: [flowIn(), input('id', 'Emitter ID', 'string', '')],
  outputs: [flowOut()],
  compile(ctx) {
    ctx.emit('vfxEmitterManager.stop', [ctx.resolveInput('id')], 'void', true);
  },
  execute(ctx) {
    ctx.bridge.call('vfxEmitterManager.stop', ctx.getInput<string>('id'));
  },
};

export const EmitterSpawn: NodeBehaviour = {
  id: 'vfx.emitterSpawn',
  name: 'Emitter Spawn',
  category: 'VFX',
  color: '#e74c3c',
  icon: 'Zap',
  description: 'Manually spawns particles from an emitter.',
  keywords: ['emitter', 'spawn', 'burst'],
  inputs: [flowIn(), input('id', 'Emitter ID', 'string', ''), input('count', 'Count', 'i32', 10)],
  outputs: [flowOut()],
  compile(ctx) {
    ctx.emit('vfxEmitterManager.spawn', [ctx.resolveInput('id'), ctx.resolveInput('count')], 'void', true);
  },
  execute(ctx) {
    ctx.bridge.call('vfxEmitterManager.spawn', ctx.getInput<string>('id'), ctx.getInput<number>('count'));
  },
};

// ============================================================================
// EMITTER VARIABLES
// ============================================================================

export const SetEmitterFloat: NodeBehaviour = {
  id: 'vfx.setEmitterFloat',
  name: 'Set Emitter Float',
  category: 'VFX',
  color: '#e74c3c',
  icon: 'Sliders',
  description: 'Sets a float variable on a GPU emitter.',
  keywords: ['emitter', 'variable', 'float'],
  inputs: [flowIn(), input('id', 'Emitter ID', 'string', ''), input('index', 'Index', 'i32', 0), input('value', 'Value', 'f32', 0)],
  outputs: [flowOut()],
  compile(ctx) {
    ctx.emit('vfxEmitterManager.setFloat', [ctx.resolveInput('id'), ctx.resolveInput('index'), ctx.resolveInput('value')], 'void', true);
  },
  execute(ctx) {
    ctx.bridge.call('vfxEmitterManager.setFloat', ctx.getInput<string>('id'), ctx.getInput<number>('index'), ctx.getInput<number>('value'));
  },
};

export const SetEmitterVec2: NodeBehaviour = {
  id: 'vfx.setEmitterVec2',
  name: 'Set Emitter Vec2',
  category: 'VFX',
  color: '#e74c3c',
  icon: 'Sliders',
  description: 'Sets a vec2 variable on a GPU emitter.',
  keywords: ['emitter', 'variable', 'vector'],
  inputs: [
    flowIn(),
    input('id', 'Emitter ID', 'string', ''),
    input('index', 'Index', 'i32', 0),
    input('x', 'X', 'f32', 0),
    input('y', 'Y', 'f32', 0),
  ],
  outputs: [flowOut()],
  compile(ctx) {
    ctx.emit(
      'vfxEmitterManager.setVec2',
      [ctx.resolveInput('id'), ctx.resolveInput('index'), ctx.resolveInput('x'), ctx.resolveInput('y')],
      'void',
      true,
    );
  },
  execute(ctx) {
    ctx.bridge.call('vfxEmitterManager.setVec2', ctx.getInput<string>('id'), ctx.getInput<number>('index'), ctx.getInput<number>('x'), ctx.getInput<number>('y'));
  },
};

export const SetEmitterVec3: NodeBehaviour = {
  id: 'vfx.setEmitterVec3',
  name: 'Set Emitter Vec3',
  category: 'VFX',
  color: '#e74c3c',
  icon: 'Sliders',
  description: 'Sets a vec3 variable on a GPU emitter.',
  keywords: ['emitter', 'variable', 'vector'],
  inputs: [
    flowIn(),
    input('id', 'Emitter ID', 'string', ''),
    input('index', 'Index', 'i32', 0),
    input('x', 'X', 'f32', 0),
    input('y', 'Y', 'f32', 0),
    input('z', 'Z', 'f32', 0),
  ],
  outputs: [flowOut()],
  compile(ctx) {
    ctx.emit(
      'vfxEmitterManager.setVec3',
      [ctx.resolveInput('id'), ctx.resolveInput('index'), ctx.resolveInput('x'), ctx.resolveInput('y'), ctx.resolveInput('z')],
      'void',
      true,
    );
  },
  execute(ctx) {
    ctx.bridge.call('vfxEmitterManager.setVec3', ctx.getInput<string>('id'), ctx.getInput<number>('index'), ctx.getInput<number>('x'), ctx.getInput<number>('y'), ctx.getInput<number>('z'));
  },
};

export const SetEmitterColor: NodeBehaviour = {
  id: 'vfx.setEmitterColor',
  name: 'Set Emitter Color',
  category: 'VFX',
  color: '#e74c3c',
  icon: 'Palette',
  description: 'Sets a color variable on a GPU emitter.',
  keywords: ['emitter', 'variable', 'color', 'tint'],
  inputs: [
    flowIn(),
    input('id', 'Emitter ID', 'string', ''),
    input('index', 'Index', 'i32', 0),
    input('r', 'R', 'f32', 1),
    input('g', 'G', 'f32', 1),
    input('b', 'B', 'f32', 1),
    input('a', 'A', 'f32', 1),
  ],
  outputs: [flowOut()],
  compile(ctx) {
    ctx.emit(
      'vfxEmitterManager.setColor',
      [ctx.resolveInput('id'), ctx.resolveInput('index'), ctx.resolveInput('r'), ctx.resolveInput('g'), ctx.resolveInput('b'), ctx.resolveInput('a')],
      'void',
      true,
    );
  },
  execute(ctx) {
    ctx.bridge.call('vfxEmitterManager.setColor', ctx.getInput<string>('id'), ctx.getInput<number>('index'), ctx.getInput<number>('r'), ctx.getInput<number>('g'), ctx.getInput<number>('b'), ctx.getInput<number>('a'));
  },
};

// ============================================================================
// EMITTER STATE
// ============================================================================

export const GetEmitterStats: NodeBehaviour = {
  id: 'vfx.getEmitterStats',
  name: 'Get Emitter Stats',
  category: 'VFX',
  isPure: true,
  color: '#e74c3c',
  icon: 'BarChart',
  description: 'Gets emitter manager statistics.',
  keywords: ['emitter', 'stats', 'count', 'particles'],
  inputs: [],
  outputs: [output('emitterCount', 'Emitter Count', 'i32'), output('particleCount', 'Particle Count', 'i32')],
  compile(ctx) {
    return ctx.emit('vfxEmitterManager.getStats', [], 'any');
  },
  execute(ctx) {
    const r = ctx.bridge.call('vfxEmitterManager.getStats') as { emitterCount?: number; particleCount?: number } | null;
    ctx.setOutput('emitterCount', r?.emitterCount ?? 0);
    ctx.setOutput('particleCount', r?.particleCount ?? 0);
  },
};

export const GetEmitterParticleCount: NodeBehaviour = {
  id: 'vfx.getEmitterParticleCount',
  name: 'Get Emitter Particle Count',
  category: 'VFX',
  isPure: true,
  color: '#e74c3c',
  icon: 'Hash',
  description: 'Gets the number of alive particles in an emitter.',
  keywords: ['emitter', 'particles', 'count', 'alive'],
  inputs: [input('id', 'Emitter ID', 'string', '')],
  outputs: [output('count', 'Count', 'i32')],
  compile(ctx) {
    return ctx.emit('bridge:getEmitterParticleCount', [ctx.resolveInput('id')], 'i32');
  },
  execute(ctx) {
    ctx.setOutput('count', ctx.bridge.call('bridge:getEmitterParticleCount', ctx.getInput<string>('id')) as number ?? 0);
  },
};

// ============================================================================
// SCREEN EFFECTS (Post-Processing)
// ============================================================================

export const ScreenShake: NodeBehaviour = {
  id: 'vfx.screenShake',
  name: 'Screen Shake',
  category: 'VFX',
  color: '#e74c3c',
  icon: 'Vibrate',
  description: 'Applies a screen shake effect.',
  keywords: ['shake', 'rumble', 'impact', 'camera'],
  inputs: [flowIn(), input('intensity', 'Intensity', 'f32', 1), input('duration', 'Duration (s)', 'f32', 0.3)],
  outputs: [flowOut()],
  compile(ctx) {
    ctx.emit('bridge:screenShake', [ctx.resolveInput('intensity'), ctx.resolveInput('duration')], 'void', true);
  },
  execute(ctx) {
    ctx.bridge.call('bridge:screenShake', ctx.getInput<number>('intensity'), ctx.getInput<number>('duration'));
  },
};

export const ScreenFlash: NodeBehaviour = {
  id: 'vfx.screenFlash',
  name: 'Screen Flash',
  category: 'VFX',
  color: '#e74c3c',
  icon: 'Lightbulb',
  description: 'Flashes the screen with a color.',
  keywords: ['flash', 'blink', 'hit', 'white'],
  inputs: [flowIn(), input('color', 'Color', 'color', 0xFFFFFFFF), input('duration', 'Duration (s)', 'f32', 0.1)],
  outputs: [flowOut()],
  compile(ctx) {
    ctx.emit('bridge:screenFlash', [ctx.resolveInput('color'), ctx.resolveInput('duration')], 'void', true);
  },
  execute(ctx) {
    ctx.bridge.call('bridge:screenFlash', ctx.getInput('color'), ctx.getInput<number>('duration'));
  },
};

export const SetChromaticAberration: NodeBehaviour = {
  id: 'vfx.setChromaticAberration',
  name: 'Set Chromatic Aberration',
  category: 'VFX',
  color: '#e74c3c',
  icon: 'Aperture',
  description: 'Sets the chromatic aberration post-processing intensity.',
  keywords: ['postfx', 'rgb', 'split', 'glitch'],
  inputs: [flowIn(), input('intensity', 'Intensity', 'f32', 0)],
  outputs: [flowOut()],
  compile(ctx) {
    ctx.emit('bridge:setChromaticAberration', [ctx.resolveInput('intensity')], 'void', true);
  },
  execute(ctx) {
    ctx.bridge.call('bridge:setChromaticAberration', ctx.getInput<number>('intensity'));
  },
};

export const SetVignette: NodeBehaviour = {
  id: 'vfx.setVignette',
  name: 'Set Vignette',
  category: 'VFX',
  color: '#e74c3c',
  icon: 'Circle',
  description: 'Sets the vignette post-processing effect.',
  keywords: ['postfx', 'darken', 'edges'],
  inputs: [flowIn(), input('intensity', 'Intensity', 'f32', 0), input('smoothness', 'Smoothness', 'f32', 0.5)],
  outputs: [flowOut()],
  compile(ctx) {
    ctx.emit('bridge:setVignette', [ctx.resolveInput('intensity'), ctx.resolveInput('smoothness')], 'void', true);
  },
  execute(ctx) {
    ctx.bridge.call('bridge:setVignette', ctx.getInput<number>('intensity'), ctx.getInput<number>('smoothness'));
  },
};

export const SetBloom: NodeBehaviour = {
  id: 'vfx.setBloom',
  name: 'Set Bloom',
  category: 'VFX',
  color: '#e74c3c',
  icon: 'Sun',
  description: 'Sets the bloom post-processing effect.',
  keywords: ['postfx', 'glow', 'hdr', 'bright'],
  inputs: [flowIn(), input('intensity', 'Intensity', 'f32', 0), input('threshold', 'Threshold', 'f32', 1)],
  outputs: [flowOut()],
  compile(ctx) {
    ctx.emit('bridge:setBloom', [ctx.resolveInput('intensity'), ctx.resolveInput('threshold')], 'void', true);
  },
  execute(ctx) {
    ctx.bridge.call('bridge:setBloom', ctx.getInput<number>('intensity'), ctx.getInput<number>('threshold'));
  },
};

// ============================================================================
// EXPORT
// ============================================================================

export const VFX_NODES: NodeBehaviour[] = [
  // CPU VFX System
  SpawnVFX,
  MuzzleFlash,
  BulletImpact,
  Explosion,
  Debris,
  Smoke,
  Fire,
  StopEffect,
  ClearAllEffects,
  GetEffectCount,
  // GPU Emitter Manager
  CreateEmitter,
  DestroyEmitter,
  DestroyAllEmitters,
  SetEmitterPosition,
  SetEmitterScale,
  PlayEmitter,
  StopEmitter,
  EmitterSpawn,
  // Emitter Variables
  SetEmitterFloat,
  SetEmitterVec2,
  SetEmitterVec3,
  SetEmitterColor,
  // Emitter State
  GetEmitterStats,
  GetEmitterParticleCount,
  // Screen Effects
  ScreenShake,
  ScreenFlash,
  SetChromaticAberration,
  SetVignette,
  SetBloom,
];
